/**
 * Open-redirect guard for `?next=` params.
 *
 * Sign-in / OTP / signup / verify-email flows all carry a `?next=` query
 * param so the user lands back where they started (most commonly a share-
 * link verify detour). Without sanitization, an attacker can craft a
 * phishing link like `/login?next=//attacker.example/...` and bounce a
 * freshly-signed-in user off-site under the host's TLS chain.
 *
 * The naive substring check (`startsWith('/') && !startsWith('//')`) is
 * NOT enough — the WHATWG URL parser (used by `window.location.href = X`
 * and `new URL(X, base)`) normalises `\` to `/` and strips control
 * characters (\t \r \n) before resolving the host. Three concrete bypass
 * shapes survive the prefix check then escape the origin:
 *
 *   `/\evil.com/x`      → 'https://evil.com/x'  (backslash normalised)
 *   `/\t/evil.com/x`    → 'https://evil.com/x'  (tab stripped)
 *   `/\r/evil.com/x`    → 'https://evil.com/x'  (CR stripped)
 *
 * The load-bearing check below parses the raw input against a sentinel
 * placeholder origin and rejects any result whose host changed. That
 * matches every browser's URL spec interpretation by construction, so
 * future normalisation quirks are caught the same way.
 *
 * Lives in a shared module so LoginPage, EmailOtpSignIn, SignupPage,
 * VerifyEmailPage all share one source of truth — drift between these
 * would mean one path is exploitable while the others aren't.
 */
const SAFE_NEXT_PLACEHOLDER_ORIGIN = 'https://safe-next.invalid'

/**
 * Where a rejected or absent `?next=` falls back to — i.e. the POST-LOGIN
 * landing, which is what every one of these flows ends in.
 *
 * It is `/`, and the shell decides from there. These flows run with no session
 * (sign-in, sign-up, the verify-email link), so they cannot know what home is:
 * the landing depends on the TENANT'S MODE (an identity-only tenant always
 * lands on the launcher — `landingViewFor`) and on which tenant is active, and
 * neither is known until `my-tenants` answers. The `/` route waits for exactly
 * that (`tenantsSettled`) and redirects with `landingPathFor(prefix, mode)`;
 * the mobile shell's catch-all does the same.
 *
 * It was `landingPath('/~')` — the deployment's landing in the personal tenant,
 * mode-blind. With identity-only personal tenants on a chat-landing deployment,
 * every new account verified its email onto `/~/chat`, the home the shell's own
 * `/` route sends them away from (#1023 follow-up; SignUpForm also put that
 * path in the verification link as `next=`).
 *
 * SAFETY, since this is the module that exists for it: the fallback is a
 * literal, never derived from `raw` or from any store, so it cannot be steered
 * and cannot leave the origin. (The poisoned-landing-cache cases in
 * `safe-next.test.ts` stay as a guard that it never reads one again.)
 */
const home = () => '/'

export function safeNext(raw: string | null): string {
  if (!raw) return home()

  // Cheap early-exits: still reject the most obvious bypass shapes
  // before constructing a URL. These also serve as documentation of the
  // surface — anyone copy-pasting the function later sees the explicit
  // list of attack shapes rather than just an opaque parser dance.
  if (!raw.startsWith('/')) return home()
  if (raw.startsWith('//')) return home()
  if (/[\\\t\r\n]/.test(raw)) return home()

  // Parser-based gate: resolve against a placeholder origin and verify
  // the resolved URL stays on it. Any host-escape via normalisation we
  // didn't anticipate above will land somewhere other than the sentinel
  // and trip the reject. Return the re-assembled path so downstream
  // consumers see the canonical form (handles e.g. `/a/../b` collapse).
  try {
    const resolved = new URL(raw, SAFE_NEXT_PLACEHOLDER_ORIGIN)
    if (resolved.origin !== SAFE_NEXT_PLACEHOLDER_ORIGIN) return home()
    return resolved.pathname + resolved.search + resolved.hash
  } catch {
    return home()
  }
}
